import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';
import { SmartVideoPlayer } from '../components/SharedUI';

const { LuVideo, LuQuote, LuUser } = LuIcons;

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      const { data } = await supabase
        .from('customer_reviews')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      
      if (data) setReviews(data);
      setLoading(false);
    };
    fetchReviews();
  }, []);

  return (
    <div className="flex-1 w-full max-w-7xl mx-auto px-4 py-8">
      <div className="text-center mb-10">
        <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <SafeIcon icon={LuVideo} className="w-8 h-8 text-indigo-600" />
        </div>
        <h1 className="text-3xl font-black text-slate-900 mb-2">آراء زبائننا</h1>
        <p className="text-slate-500">شاهد تجارب عملائنا الكرام مع خدماتنا</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : reviews.length === 0 ? (
        <div className="bg-white rounded-2xl p-16 text-center border border-slate-200">
          <SafeIcon icon={LuVideo} className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-bold">لا توجد مراجعات حالياً.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((review, i) => {
            const type = review.review_type || 'video';

            return (
              <motion.div 
                key={review.id} 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col"
              >
                {/* الريندر الخاص بالفيديو (يستخدم المشغل الذكي) */}
                {type === 'video' && (
                  <>
                    <div className="relative aspect-[9/16] bg-black">
                      <SmartVideoPlayer url={review.video_url} title={review.customer_name} />
                    </div>
                    <div className="p-4 bg-slate-50 border-t border-slate-100 flex-1">
                      <h3 className="font-black text-slate-800 text-lg mb-1">{review.customer_name}</h3>
                      {review.car_purchased && (
                        <p className="text-sm font-bold text-indigo-600 bg-indigo-50 inline-block px-2 py-1 rounded-lg">
                          السيارة: {review.car_purchased}
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* الريندر الخاص بالصورة */}
                {type === 'image' && (
                  <>
                    <div className="relative aspect-square sm:aspect-[4/3] bg-slate-100 p-2">
                      <img src={review.content} alt={review.customer_name} className="w-full h-full object-contain rounded-xl" />
                    </div>
                    <div className="p-4 bg-slate-50 border-t border-slate-100 mt-auto">
                      <h3 className="font-black text-slate-800 text-lg mb-1">{review.customer_name}</h3>
                      {review.car_purchased && (
                        <p className="text-sm font-bold text-indigo-600 bg-indigo-50 inline-block px-2 py-1 rounded-lg">
                          السيارة: {review.car_purchased}
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* الريندر الخاص بالنص */}
                {type === 'text' && (
                  <div className="p-6 flex flex-col h-full bg-gradient-to-b from-white to-slate-50">
                    <SafeIcon icon={LuQuote} className="w-10 h-10 text-indigo-100 mb-4" />
                    <p className="text-slate-700 font-medium leading-relaxed mb-6 flex-1 text-lg">"{review.content}"</p>
                    
                    <div className="flex items-center gap-3 pt-4 border-t border-slate-100 mt-auto">
                      <div className="w-12 h-12 bg-indigo-50 rounded-full flex items-center justify-center overflow-hidden border border-indigo-100 flex-shrink-0">
                        {review.avatar_url ? (
                          <img src={review.avatar_url} alt={review.customer_name} className="w-full h-full object-cover" />
                        ) : (
                          <SafeIcon icon={LuUser} className="w-6 h-6 text-indigo-400" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-black text-slate-800">{review.customer_name}</h3>
                        {review.car_purchased && (
                          <p className="text-xs font-bold text-indigo-600 mt-0.5">
                            اشترى: {review.car_purchased}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReviewsPage;